<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import { megamenuSalesPath } from '~/data/megamenu'
import IconChevronLeft from '~/assets/icons/chevron-left.svg?component'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import IconLightning from '~/assets/icons/lightning-charge-fill.svg?component'
import { sendMenuClick } from '~/composables/useGtmEvents'

const props = defineProps<{
  item: MegamenuNavItem
  title: string
  /** When true, sections expand as accordion in place instead of emitting openSection */
  expandInPanel?: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'openSection', index: number): void
  (e: 'navbarClose'): void
}>()

const localePath = useLocalePath()

/** Index of the section currently expanded in accordion mode; null = none */
const expandedSectionIndex = ref<number | null>(null)

function isExternal(href: string) {
  return href.startsWith('http')
}

function resolveHref(href: string) {
  return isExternal(href) ? href : localePath(href)
}

function sectionHasContent(section: MegamenuSection) {
  return (
    (section.items?.length ?? 0) > 0 ||
    (section.subSections?.length ?? 0) > 0 ||
    (section.title === 'Sales')
  )
}

function isSalesSection(section: MegamenuSection) {
  return section.title === 'Sales'
}

function toggleAccordion(index: number) {
  expandedSectionIndex.value = expandedSectionIndex.value === index ? null : index
}

function handlePrimaryLinkClick() {
  if (props.item.primaryLink) {
    sendMenuClick({
      menuName: props.item.label,
      itemLabel: props.item.primaryLink.label,
      itemPath: props.item.primaryLink.href,
    })
  }
  emit('navbarClose')
}

function handleSalesClick() {
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: 'Sales',
    itemLabel: 'Sales',
    itemPath: megamenuSalesPath,
  })
  emit('navbarClose')
}

function handleAccordionSubSectionItemClick(
  section: MegamenuSection,
  sub: { title: string },
  link: { label: string; href?: string }
) {
  if (!link.href) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: section.title,
    subSectionTitle: sub.title,
    itemLabel: link.label,
    itemPath: link.href,
  })
  emit('navbarClose')
}

function handleAccordionFlatItemClick(
  section: MegamenuSection,
  link: { label: string; href?: string }
) {
  if (!link.href) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: section.title,
    itemLabel: link.label,
    itemPath: link.href,
  })
  emit('navbarClose')
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <header class="flex items-center gap-2 shrink-0 border-b border-gray-200 pb-2 px-4">
      <button
        type="button"
        class="p-2 -ml-2 text-stone-700 hover:bg-gray-100 rounded-full"
        aria-label="Back"
        @click="emit('back')"
      >
        <IconChevronLeft class="h-5 w-5" />
      </button>
      <h2 class="text-lg text-center w-full font-semibold text-stone-900">
        {{ title }}
      </h2>
    </header>
    <div class="flex-1 overflow-auto min-h-0 px-4">
      <!-- Primary "All X" / "Go to Magazine" link -->
      <div v-if="item.primaryLink" class="border-b border-gray-100">
        <a
          v-if="isExternal(item.primaryLink.href)"
          :href="resolveHref(item.primaryLink.href)"
          class="flex items-center justify-between py-3 text-stone-800 no-underline hover:text-teal-600"
          target="_blank"
          rel="noopener"
          @click="handlePrimaryLinkClick"
        >
          {{ item.primaryLink.label }}
        </a>
        <NuxtLinkLocale
          v-else
          :to="item.primaryLink.href"
          class="flex items-center justify-between py-3 text-stone-800 no-underline hover:text-teal-600"
          @click="handlePrimaryLinkClick"
        >
          {{ item.primaryLink.label }}
        </NuxtLinkLocale>
      </div>
      <!-- Section list: accordion when expandInPanel, else buttons that open panel -->
      <nav class="flex flex-col">
        <template
          v-for="(section, index) in (item.sections ?? [])"
          :key="section.title"
        >
          <template v-if="sectionHasContent(section)">
          <NuxtLinkLocale
            v-if="isSalesSection(section)"
            :to="megamenuSalesPath"
            class="flex items-center gap-2 rounded-lg bg-orange-50 px-4 py-3  text-red-500 no-underline font-medium"
            @click="handleSalesClick"
          >
            <IconLightning class="h-5 w-5 shrink-0 text-red-500" />
            {{ section.title }}
          </NuxtLinkLocale>
          <template v-else>
            <!-- Accordion mode: toggle to show content below -->
            <button
              v-if="expandInPanel"
              type="button"
              class="flex w-full items-center justify-between py-3 text-left font-medium text-stone-800 border-b border-gray-100"
              :aria-expanded="expandedSectionIndex === index"
              @click="toggleAccordion(index)"
            >
              {{ section.title }}
              <IconChevronRight
                class="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-90': expandedSectionIndex === index }"
              />
            </button>
            <!-- Panel mode: open section in separate panel -->
            <button
              v-else
              type="button"
              class="flex w-full items-center justify-between py-3 text-left font-medium text-stone-800 border-b border-gray-100"
              @click="emit('openSection', index)"
            >
              {{ section.title }}
              <IconChevronRight class="h-5 w-5 shrink-0 text-gray-400" />
            </button>
            <!-- Accordion content -->
            <div
              v-if="expandInPanel && expandedSectionIndex === index"
              class="border-b border-gray-100"
            >
              <!-- subSections (e.g. Top Kategorien) -->
              <template v-if="section.subSections?.length">
                <div
                  v-for="sub in section.subSections"
                  :key="sub.title"
                  class="border-t border-gray-100 first:border-t-0"
                >
                  <div class="py-2 text-sm font-medium text-stone-500">
                    {{ sub.title }}
                  </div>
                  <ul>
                    <li
                      v-for="(link, i) in sub.items?.filter((l) => l?.label)"
                      :key="`${sub.title}-${i}`"
                      class="border-t border-gray-100"
                    >
                      <a
                        v-if="link.href && isExternal(link.href)"
                        :href="resolveHref(link.href)"
                        class="flex items-center justify-between py-2.5 pl-6 text-stone-800 no-underline hover:text-teal-600"
                        target="_blank"
                        rel="noopener"
                        @click="handleAccordionSubSectionItemClick(section, sub, link)"
                      >
                        {{ link.label }}
                        <IconChevronRight class="h-4 w-4 text-gray-400" />
                      </a>
                      <NuxtLinkLocale
                        v-else-if="link.href"
                        :to="link.href"
                        class="flex items-center justify-between py-2.5 pl-6 text-stone-800 no-underline hover:text-teal-600"
                        @click="handleAccordionSubSectionItemClick(section, sub, link)"
                      >
                        {{ link.label }}
                        <IconChevronRight class="h-4 w-4 text-gray-400" />
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </div>
              </template>
              <!-- items -->
              <template v-else-if="section.items?.length">
                <template v-if="section.items.length === 1 && section.items[0]?.href">
                  <a
                    v-if="isExternal(section.items[0].href)"
                    :href="resolveHref(section.items[0].href)"
                    class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
                    target="_blank"
                    rel="noopener"
                    @click="handleAccordionFlatItemClick(section, section.items[0])"
                  >
                    {{ section.items[0].label }}
                    <IconChevronRight class="h-4 w-4 text-gray-400" />
                  </a>
                  <NuxtLinkLocale
                    v-else
                    :to="section.items[0].href"
                    class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
                    @click="handleAccordionFlatItemClick(section, section.items[0])"
                  >
                    {{ section.items[0].label }}
                    <IconChevronRight class="h-4 w-4 text-gray-400" />
                  </NuxtLinkLocale>
                </template>
                <ul v-else>
                  <li
                    v-for="(link, i) in section.items.filter((l) => l?.label)"
                    :key="i"
                    class="border-t border-gray-100"
                  >
                    <a
                      v-if="link.href && isExternal(link.href)"
                      :href="resolveHref(link.href)"
                      class="flex items-center justify-between py-2.5 text-stone-800 no-underline hover:text-teal-600"
                      target="_blank"
                      rel="noopener"
                      @click="handleAccordionFlatItemClick(section, link)"
                    >
                      {{ link.label }}
                    </a>
                    <NuxtLinkLocale
                      v-else-if="link.href"
                      :to="link.href"
                      class="flex items-center justify-between py-2.5 text-stone-800 no-underline hover:text-teal-600"
                      @click="handleAccordionFlatItemClick(section, link)"
                    >
                      {{ link.label }}
                    </NuxtLinkLocale>
                  </li>
                </ul>
              </template>
            </div>
          </template>
          </template>
        </template>
      </nav>
    </div>
  </div>
</template>
